// Pause controller — extracted verbatim from GameScene.ts (FACE&FURY S2 SPLIT).
// Behavior-frozen: method bodies are byte-identical to the monolith; only the signatures were
// rewritten to `export function name(this: GameScene, ...)` and bound back onto the scene as fields.
import Phaser from 'phaser';
import { FONT, PALETTE, hex } from '../keys';
import { audio } from '../audio';
import { modal } from '../ui';
import { ctaButton, secondaryButton } from '../ui/kit';
import { openSettings } from '../settingsPanel';
import { openOnboarding } from '../onboarding';
import type { GameScene } from '../scenes/GameScene';
import { AI, PLAYER, compactLabel } from './types';


  // --- pause menu (M3) ------------------------------------------------------
export function togglePause(this: GameScene): void {
    if (this.ended) return;
    this.cancelPendingSteps(); // pausing (or resuming) aborts any in-flight glide.
    if (this.paused) this.resumeGame();
    else this.openPause();
  }


  /** Freeze the sim + show the pause modal (Resume / How to play / Settings / Quit to menu). */
export function openPause(this: GameScene): void {
    if (this.ended || this.paused) return;
    this.paused = true;
    audio().playSfx('uiTap');
    audio().setIntensity(0); // calm the music bed while paused
    const snap = this.sim.snapshot();
    const human = snap.players.find((p) => p.id === PLAYER);
    const rival = snap.players.find((p) => p.id === AI);
    const eaten = human?.eatenCorrect ?? 0;
    const quota = Math.max(1, snap.quota);
    const toWin = Math.max(0, quota - eaten);
    const score = human?.score ?? 0;
    const lives = human?.lives ?? 0;
    const lead = rival ? score - rival.score : 0;

    // Tactical-OLED pause: titleless modal so the shared X close cannot strand the sim in paused state.
    const H = this.scale.height;
    const pauseH = Math.min(430, Math.max(416, H * 0.5), H - 54);
    const m = modal(this, { title: '', widthFrac: 0.88, heightFrac: pauseH / H, veilClose: false, railGrad: [PALETTE.scan, PALETTE.player] });
    this.pauseLayer = m.layer;
    const { card, cardW, cardH } = m;
    const bw = cardW - 48;
    const veil = m.layer.list.find((o): o is Phaser.GameObjects.Rectangle => o instanceof Phaser.GameObjects.Rectangle);
    veil?.setFillStyle(PALETTE.void, 0.92);

    const top = -cardH / 2;
    const titleY = top + 30;
    const title = this.add
      .text(0, titleY, 'PAUSED', {
        fontFamily: FONT.display,
        fontSize: `${Math.max(20, Math.round(cardW * 0.055))}px`,
        color: hex(PALETTE.text),
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const divider = this.add.rectangle(0, top + 54, cardW - 36, 1, PALETTE.line).setOrigin(0.5);
    card.add([title, divider]);

    const statusY = top + 78;
    const status = this.add.graphics();
    status.fillStyle(PALETTE.tile, 1);
    status.fillRoundedRect(-bw / 2, statusY - 18, bw, 36, 9);
    status.lineStyle(1.2, PALETTE.scan, 0.65);
    status.strokeRoundedRect(-bw / 2, statusY - 18, bw, 36, 9);
    const statusDot = this.add.circle(-bw / 2 + 17, statusY, 4.5, PALETTE.correct, 1);
    const statusText = this.add
      .text(-bw / 2 + 30, statusY, 'GAME PAUSED', {
        fontFamily: FONT.display,
        fontSize: '12px',
        color: hex(PALETTE.scan),
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);
    const routeText = this.add
      .text(bw / 2 - 14, statusY, 'POSITION SAVED', {
        fontFamily: FONT.display,
        fontSize: '12px',
        color: hex(PALETTE.dim),
        fontStyle: 'bold',
      })
      .setOrigin(1, 0.5);
    card.add([status, statusDot, statusText, routeText]);

    const missionTop = top + 106;
    const missionH = 120;
    const mission = this.add.graphics();
    mission.fillStyle(PALETTE.surface, 1);
    mission.fillRoundedRect(-bw / 2, missionTop, bw, missionH, 12);
    mission.lineStyle(1.2, PALETTE.lineStrong, 0.95);
    mission.strokeRoundedRect(-bw / 2, missionTop, bw, missionH, 12);
    mission.fillStyle(PALETTE.player, 0.95);
    mission.fillRoundedRect(-bw / 2 + 1, missionTop + 9, 4, missionH - 18, 2);

    const topic = compactLabel(this.topicLabel || snap.topic || this.topic || 'Trivia', 32).toUpperCase();
    const rule = compactLabel(snap.rule, cardW < 360 ? 58 : 72);
    const topicText = this.add
      .text(-bw / 2 + 18, missionTop + 21, topic, {
        fontFamily: FONT.display,
        fontSize: `${cardW < 350 ? 14 : 15}px`,
        color: hex(PALETTE.text),
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);
    const ruleText = this.add
      .text(-bw / 2 + 18, missionTop + 44, rule, {
        fontFamily: FONT.family,
        fontSize: `${cardW < 350 ? 11 : 12}px`,
        color: hex(PALETTE.dim),
        wordWrap: { width: bw - 36 },
      })
      .setOrigin(0, 0.5);
    const barW = bw - 36;
    const barY = missionTop + 70;
    const barTrack = this.add.rectangle(-bw / 2 + 18, barY, barW, 8, PALETTE.tileHot, 1).setOrigin(0, 0.5);
    const barFill = this.add.rectangle(-bw / 2 + 18, barY, Math.max(8, barW * Math.min(1, eaten / quota)), 8, PALETTE.correct, 1).setOrigin(0, 0.5);

    const metricY = missionTop + 95;
    const metricW = (bw - 36 - 12) / 3;
    const metricStart = -bw / 2 + 18;
    const metric = (i: number, label: string, value: string, color: number): Phaser.GameObjects.Container => {
      const x = metricStart + i * (metricW + 6) + metricW / 2;
      const c = this.add.container(x, metricY);
      const g = this.add.graphics();
      g.fillStyle(PALETTE.tile, 1);
      g.fillRoundedRect(-metricW / 2, -17, metricW, 34, 7);
      g.lineStyle(1, PALETTE.line, 1);
      g.strokeRoundedRect(-metricW / 2, -17, metricW, 34, 7);
      const v = this.add
        .text(0, -5, value, { fontFamily: FONT.display, fontSize: `${cardW < 350 ? 13 : 14}px`, color: hex(color), fontStyle: 'bold' })
        .setOrigin(0.5);
      const l = this.add
        .text(0, 10, label, { fontFamily: FONT.display, fontSize: `${cardW < 350 ? 10 : 11}px`, color: hex(PALETTE.textDim), fontStyle: 'bold' })
        .setOrigin(0.5);
      c.add([g, v, l]);
      return c;
    };
    const leadLabel = rival ? (lead >= 0 ? `+${lead}` : `${lead}`) : `${lives}`;
    const leadName = rival ? (lead >= 0 ? 'LEAD' : 'DOWN') : 'LIVES';
    card.add([
      mission,
      topicText,
      ruleText,
      barTrack,
      barFill,
      metric(0, 'SCORE', score.toLocaleString('en-US'), PALETTE.gold),
      metric(1, 'TO WIN', `${toWin}`, toWin <= 1 ? PALETTE.armed : PALETTE.correct),
      metric(2, leadName, leadLabel, rival ? (lead >= 0 ? PALETTE.player : PALETTE.rival) : PALETTE.hazard),
    ]);

    const actionY = missionTop + missionH + 34;
    const resume = ctaButton(this, 0, actionY, 'RESUME', () => this.resumeGame(), { width: bw, height: 56, icon: 'play' });
    const halfW = (bw - 12) / 2;
    const howTo = secondaryButton(
      this,
      -halfW / 2 - 6,
      actionY + 60,
      'HOW TO PLAY',
      () => openOnboarding(this, () => {}),
      { width: halfW, height: 46, icon: 'question', fontPx: cardW < 350 ? 12 : 13 },
    );
    const settingsBtn = secondaryButton(this, halfW / 2 + 6, actionY + 60, 'SETTINGS', () => openSettings(this), {
      width: halfW,
      height: 46,
      icon: 'gear',
      fontPx: cardW < 350 ? 12 : 13,
    });
    const quit = secondaryButton(
      this,
      0,
      actionY + 116,
      'QUIT TO MENU',
      () => {
        audio().stopMusic();
        this.scene.start('Menu');
      },
      { width: bw, height: 46, icon: 'xCircle', textColor: 0xff7b91, fontPx: 17 },
    );
    // Recolour the quit border to hazard@0.4 so it reads as a DANGER action (kit border is lineStrong).
    const quitPlate = this.add.graphics();
    quitPlate.fillStyle(PALETTE.hazard, 0.04);
    quitPlate.fillRoundedRect(-bw / 2, -23, bw, 46, 10);
    quitPlate.lineStyle(1.35, PALETTE.hazard, 0.72);
    quitPlate.strokeRoundedRect(-bw / 2, -23, bw, 46, 10);
    quit.addAt(quitPlate, 1);

    card.add([resume, howTo, settingsBtn, quit]);
  }


export function resumeGame(this: GameScene): void {
    if (!this.paused) return;
    this.paused = false;
    // time.now advanced through the pause (update() early-returns but the clock runs) — re-stamp so
    // the idle nudge can't fire on the first frame after a >=9s pause.
    this.lastHumanInputMs = this.time.now;
    audio().playSfx('uiConfirm');
    this.pauseLayer?.destroy();
    this.pauseLayer = undefined;
  }
